import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, Loader2, Pencil } from 'lucide-react'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { OTPInput } from '@/components/auth/OTPInput'
import { useUiStore } from '@/store/uiStore'
import { mapApiUser, useAuthStore } from '@/store/authStore'
import { webApi } from '@/services/webApi'
import { trackEvent } from '@/lib/analytics'
import { AUTH_EVENT_PARAMS, authEventName } from '@/lib/analytics/authEvents'
import { PATHS } from '@/routes/paths'
import { cn } from '@/lib/utils'
import axios from 'axios'

type Step = 'mobile' | 'otp' | 'profile'

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message
    if (typeof msg === 'string' && msg.trim()) return msg
  }
  return fallback
}

function maskMobile(mobile: string) {
  if (mobile.length < 10) return mobile
  return `${mobile.slice(0, 2)}****${mobile.slice(6)}`
}

export function AuthModal() {
  const {
    isAuthModalOpen,
    setAuthModalOpen,
    authModalMobilePrefill,
    authModalStartAtOtp,
    clearAuthModalContext,
  } = useUiStore()
  const { setAuth } = useAuthStore()
  const [step, setStep] = useState<Step>('mobile')
  const [mobile, setMobile] = useState('')
  const [otp, setOtp] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resendIn, setResendIn] = useState(0)
  const verifyInFlight = useRef(false)

  const reset = () => {
    setStep('mobile')
    setMobile('')
    setOtp('')
    setName('')
    setEmail('')
    setError('')
    setResendIn(0)
    clearAuthModalContext()
  }

  useEffect(() => {
    if (resendIn <= 0) return
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  useEffect(() => {
    if (!isAuthModalOpen) return
    if (!authModalMobilePrefill) return

    setMobile(authModalMobilePrefill)
    setError('')

    if (authModalStartAtOtp && authModalMobilePrefill.length === 10) {
      setStep('otp')
      setOtp('')
      setResendIn(30)
    } else {
      setStep('mobile')
    }
  }, [isAuthModalOpen, authModalMobilePrefill, authModalStartAtOtp])

  const handleClose = (open: boolean) => {
    setAuthModalOpen(open)
    if (!open) reset()
  }

  const sendOtp = async () => {
    if (mobile.length !== 10) {
      setError('Enter a valid 10-digit mobile number')
      return
    }
    setLoading(true)
    setError('')
    try {
      await webApi.requestOtp({ mobile })
      setOtp('')
      setStep('otp')
      setResendIn(30)
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to send OTP. Try again.'))
    } finally {
      setLoading(false)
    }
  }

  const verifyOtp = async (code?: string) => {
    if (verifyInFlight.current) return
    const entered = (code ?? otp).replace(/\D/g, '')
    if (entered.length < 6) {
      setError('Please enter the complete 6-digit OTP')
      return
    }
    verifyInFlight.current = true
    setLoading(true)
    setError('')
    try {
      const result = await webApi.verifyOtp({ mobile, otp: entered })
      setAuth(result.token, mapApiUser(result.user))
      trackEvent(authEventName(result.isNewUser === true), AUTH_EVENT_PARAMS)

      if (result.user.needsProfile) {
        setName('')
        setEmail(result.user.email || '')
        setStep('profile')
      } else {
        setAuthModalOpen(false)
        reset()
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Invalid OTP. Please try again.'))
    } finally {
      verifyInFlight.current = false
      setLoading(false)
    }
  }

  const completeProfile = async (skip = false) => {
    setLoading(true)
    setError('')
    try {
      if (!skip && name.trim()) {
        const updated = await webApi.updateProfile({
          name: name.trim(),
          email: email.trim() || undefined,
        })
        setAuth(useAuthStore.getState().token!, mapApiUser(updated))
      }
      setAuthModalOpen(false)
      reset()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save profile. Try again.'))
    } finally {
      setLoading(false)
    }
  }

  const titles: Record<Step, string> = {
    mobile: 'Login / Register',
    otp: 'Verify OTP',
    profile: 'Complete Profile',
  }

  const subtitles: Record<Step, string> = {
    mobile: 'Please enter your Phone Number',
    otp: `Enter the 6-digit code sent to +91 ${maskMobile(mobile)}`,
    profile: 'Add your details to finish setting up your account',
  }

  return (
    <Sheet open={isAuthModalOpen} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="w-full max-w-[400px] border-l border-neutral-100 p-0 shadow-2xl"
      >
        <div className="flex h-full flex-col px-8 pb-8 pt-14">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.2 }}
              className="flex flex-1 flex-col"
            >
              <div>
                <SheetTitle className="text-2xl font-bold text-primary-700">
                  {titles[step]}
                </SheetTitle>
                <p className="mt-2 text-sm text-neutral-500" data-clarity-mask="True">{subtitles[step]}</p>
              </div>

              <div className="mt-8 flex flex-1 flex-col">
                {step === 'mobile' && (
                  <div className="flex flex-1 flex-col">
                    <div
                      className={cn(
                        'relative rounded-lg border bg-white px-3 pb-3 pt-2 transition-colors',
                        error
                          ? 'border-danger-500'
                          : 'border-neutral-300 focus-within:border-primary-500',
                      )}
                    >
                      <label
                        htmlFor="auth-mobile"
                        className="text-xs font-medium text-neutral-400"
                      >
                        Phone Number
                      </label>
                      <div className="mt-1 flex items-center">
                        <span className="flex items-center gap-0.5 text-sm font-medium text-neutral-800">
                          +91
                          <ChevronDown className="h-4 w-4 text-neutral-400" aria-hidden />
                        </span>
                        <span className="mx-3 h-5 w-px bg-neutral-300" aria-hidden />
                        <Input
                          id="auth-mobile"
                          type="tel"
                          inputMode="numeric"
                          autoComplete="tel"
                          maxLength={10}
                          placeholder=""
                          className="h-9 flex-1 border-0 bg-transparent p-0 text-base shadow-none focus-visible:ring-0"
                          value={mobile}
                          onChange={(e) => {
                            setError('')
                            setMobile(e.target.value.replace(/\D/g, ''))
                          }}
                          onKeyDown={(e) => e.key === 'Enter' && sendOtp()}
                        />
                      </div>
                    </div>

                    {error && (
                      <p className="mt-2 text-sm text-danger-600">{error}</p>
                    )}

                    <div className="mt-6">
                      <Button
                        className="h-11 w-full rounded-lg text-base font-semibold"
                        size="lg"
                        onClick={sendOtp}
                        disabled={loading || mobile.length !== 10}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Please wait...
                          </>
                        ) : (
                          'Continue'
                        )}
                      </Button>
                    </div>

                    <p className="mt-auto pt-10 text-center text-xs leading-relaxed text-neutral-400">
                      By clicking you agree to{' '}
                      <Link
                        to={PATHS.terms}
                        className="text-primary-600 hover:underline"
                        onClick={() => setAuthModalOpen(false)}
                      >
                        Terms and Conditions
                      </Link>
                    </p>
                  </div>
                )}

                {step === 'otp' && (
                  <div className="flex flex-1 flex-col">
                    <button
                      type="button"
                      onClick={() => {
                        setStep('mobile')
                        setOtp('')
                        setError('')
                      }}
                      className="mb-6 flex w-fit items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Change number
                    </button>

                    <OTPInput
                      value={otp}
                      onChange={(v) => {
                        setOtp(v)
                        setError('')
                      }}
                      onComplete={(code) => verifyOtp(code)}
                      disabled={loading}
                      error={!!error}
                      align="start"
                    />

                    {error && (
                      <p className="mt-3 text-sm text-danger-600">{error}</p>
                    )}

                    <div className="mt-6">
                      <Button
                        className="h-11 w-full rounded-lg text-base font-semibold"
                        size="lg"
                        onClick={() => verifyOtp()}
                        disabled={loading || otp.length < 6}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          'Continue'
                        )}
                      </Button>
                    </div>

                    <p className="mt-4 text-center text-sm text-neutral-500">
                      Didn&apos;t receive code?{' '}
                      <button
                        type="button"
                        disabled={resendIn > 0 || loading}
                        onClick={sendOtp}
                        className={cn(
                          'font-semibold',
                          resendIn > 0
                            ? 'text-neutral-400'
                            : 'text-primary-600 hover:text-primary-700',
                        )}
                      >
                        {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend OTP'}
                      </button>
                    </p>
                  </div>
                )}

                {step === 'profile' && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="auth-name">Full Name</Label>
                      <Input
                        id="auth-name"
                        className="mt-1.5 h-11 rounded-lg"
                        placeholder="Your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="auth-email">Email (optional)</Label>
                      <Input
                        id="auth-email"
                        type="email"
                        className="mt-1.5 h-11 rounded-lg"
                        placeholder="you@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <Button
                      className="h-11 w-full rounded-lg"
                      size="lg"
                      onClick={() => completeProfile()}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Continue'}
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => completeProfile(true)}
                      disabled={loading}
                    >
                      Skip for now
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </SheetContent>
    </Sheet>
  )
}
